import { request } from 'umi';
import {
  type Notification,
  type NotificationRule,
  type UserNotificationView,
  type AdminNotificationView,
  type RuleView,
  type ActiveRuleView,
  type CreateNotificationParams,
  type UpdateNotificationParams,
  type CreateRuleParams,
  type NotificationListResponse,
  type AdminNotificationListResponse,
  type RuleListResponse,
  type StatsResponse,
  type BatchReadParams,
  type BatchOperationResult,
  type RuleExecutionResult,
  type RuleCreateResult,
  type NotificationQueryParams,
  RuleStatus,
  NotificationTargetType,
  RepeatFrequency,
  NotificationType,
  SystemType
} from './data';

export const notificationService = {
  // ==================== 用户通知接口 ====================
  
  /**
   * 获取当前用户的通知列表
   */
  getUserNotifications: async (params: NotificationQueryParams = {}): Promise<NotificationListResponse> => {
    const response = await request<API.StandardResponse<NotificationListResponse>>('/api/v1/notification/me', {
      method: 'GET',
      params: {
        skip: params.skip || 0,
        take: params.take || 50,
        unreadOnly: params.unreadOnly || false
      },
    });
    
    if (response.code === 200) {
      return response.data!;
    }
    throw new Error(response.message || '获取通知列表失败');
  },

  /**
   * 标记单个通知为已读
   */
  markAsRead: async (notificationByUserId: number): Promise<void> => {
    const response = await request<API.StandardResponse<any>>(`/api/v1/notification/read/${notificationByUserId}`, {
      method: 'POST',
    });
    
    if (response.code !== 200) {
      throw new Error(response.message || '标记已读失败');
    }
  },

  /**
   * 批量标记通知为已读
   */
  batchMarkAsRead: async (params: BatchReadParams): Promise<BatchOperationResult> => {
    const response = await request<API.StandardResponse<BatchOperationResult>>('/api/v1/notification/batch-read', {
      method: 'POST',
      data: params,
    });
    
    if (response.code === 200) {
      return response.data!;
    }
    throw new Error(response.message || '批量标记失败');
  },

  /**
   * 获取未读通知数量
   */
  getUnreadCount: async (): Promise<number> => {
    const response = await request<API.StandardResponse<{ unreadCount: number }>>('/api/v1/notification/unread-count', {
      method: 'GET',
    });
    
    if (response.code === 200) {
      return response.data!.unreadCount;
    }
    throw new Error(response.message || '获取未读数量失败');
  },

  // ==================== 管理员通知管理 ====================

  /**
   * 获取所有通知（管理员）
   */
  getAllNotifications: async (Page: number = 0, PageSize: number = 50): Promise<AdminNotificationListResponse> => {
    const response = await request<API.StandardResponse<AdminNotificationListResponse>>('/api/v1/notification/admin', {
      method: 'GET',
      params: { Page, PageSize },
    });
    
    if (response.code === 200) {
      return response.data!;
    }
    throw new Error(response.message || '获取通知列表失败');
  },

  /**
   * 创建通知
   */
  createNotification: async (data: CreateNotificationParams): Promise<number> => {
    const response = await request<API.StandardResponse<{ notificationId: number }>>('/api/v1/notification', {
      method: 'POST',
      data
    });
    
    if (response.code === 201) {
      return response.data!.notificationId;
    }
    throw new Error(response.message || '创建通知失败');
  },

  /**
   * 更新通知
   */
  updateNotification: async (id: number, data: UpdateNotificationParams): Promise<Notification> => {
    const response = await request<API.StandardResponse<Notification>>(`/api/v1/notification/${id}`, {
      method: 'PUT',
      data
    });
    
    if (response.code === 200) {
      return response.data!;
    }
    throw new Error(response.message || '更新通知失败');
  },

  // ==================== 规则管理接口 ====================

  /**
   * 获取通知的规则列表
   */
  getNotificationRules: async (): Promise<RuleView[]> => {
    const response = await request<API.StandardResponse<{ list: RuleView[] }>>(`/api/v1/notification/getRules`, {
      method: 'GET',
    });
    
    if (response.code === 200) {
      return response.data!.list;
    }
    throw new Error(response.message || '获取规则列表失败');
  },

  /**
   * 创建规则
   */
  createRule: async ( data: CreateRuleParams): Promise<RuleCreateResult> => {
    const response = await request<API.StandardResponse<RuleCreateResult>>(`/api/v1/notification/createRule`, {
      method: 'POST',
      data: {
        ...data,
        // 转换时间格式
        specificTime: data.specificTime || undefined
      }
    });
    
    if (response.code === 200) {
      return response.data!;
    }
    throw new Error(response.message || '创建规则失败');
  },
  updateRule: async ( ruleId: number,data: CreateRuleParams): Promise<RuleCreateResult> => {
    const response = await request<API.StandardResponse<RuleCreateResult>>(`/api/v1/notification/updateRule/${ruleId}`, {
      method: 'PUT',
      data: {
        ...data,
        // 转换时间格式
        specificTime: data.specificTime || undefined
      }
    });
    
    if (response.code === 200) {
      return response.data!;
    }
    throw new Error(response.message || '创建规则失败');
  },
  /**
   * 执行规则
   */
  executeRule: async (ruleId: number): Promise<RuleExecutionResult> => {
    const response = await request<API.StandardResponse<RuleExecutionResult>>(`/api/v1/notification/rules/${ruleId}/execute`, {
      method: 'POST',
    });
    
    if (response.code === 200) {
      return response.data!;
    }
    throw new Error(response.message || '执行规则失败');
  },

  /**
   * 启用规则
   */
  enableRule: async (ruleId: number): Promise<void> => {
    const response = await request<API.StandardResponse<any>>(`/api/v1/notification/rules/${ruleId}/enable`, {
      method: 'POST',
    });
    
    if (response.code !== 200) {
      throw new Error(response.message || '启用规则失败');
    }
  },

  /**
   * 禁用规则
   */
  disableRule: async (ruleId: number): Promise<void> => {
    const response = await request<API.StandardResponse<any>>(`/api/v1/notification/rules/${ruleId}/disable`, {
      method: 'POST',
    });
    
    if (response.code !== 200) {
      throw new Error(response.message || '禁用规则失败');
    }
  },

  /**
   * 获取活跃规则列表
   */
  getActiveRules: async (): Promise<ActiveRuleView[]> => {
    const response = await request<API.StandardResponse<ActiveRuleView[]>>('/api/v1/notification/rules/active', {
      method: 'GET',
    });
    
    if (response.code === 200) {
      return response.data!;
    }
    throw new Error(response.message || '获取活跃规则失败');
  },

  // ==================== 统计接口 ====================

  /**
   * 获取统计信息
   */
  getStats: async (): Promise<StatsResponse> => {
    const response = await request<API.StandardResponse<StatsResponse>>('/api/v1/notification/stats', {
      method: 'GET',
    });
    
    if (response.code === 200) {
      return response.data!;
    }
    throw new Error(response.message || '获取统计信息失败');
  }
};

// ==================== 工具函数 ====================

/**
 * 获取通知类型文本
 */
export const getNotificationTypeText = (type: NotificationType): string => {
  switch (type) {
    case NotificationType.Inform:
      return '普通通知';
    case NotificationType.Announcement:
      return '公告';
    case NotificationType.Expiration:
      return '到期提醒';
    default:
      return '未知';
  }
};

/**
 * 获取系统类型文本
 */
export const getSystemTypeText = (systemType: SystemType): string => {
  switch (systemType) {
    case SystemType.All:
      return '全站';
    case SystemType.Website:
      return '网站系统';
    case SystemType.Software:
      return '软件系统';
    default:
      return '未知';
  }
};

/**
 * 获取重复频率文本
 */
export const getRepeatFrequencyText = (frequency: RepeatFrequency): string => {
  switch (frequency) {
    case RepeatFrequency.None:
      return '不重复';
    case RepeatFrequency.Daily:
      return '每天';
    case RepeatFrequency.Weekly:
      return '每周';
    case RepeatFrequency.Monthly:
      return '每月';
    case RepeatFrequency.Hourly:
      return '每小时';
    case RepeatFrequency.EveryLogon:
      return '每次登录';
    default:
      return '未知';
  }
};

/**
 * 获取规则状态文本
 */
export const getRuleStatusText = (status: RuleStatus): string => {
  switch (status) {
    case RuleStatus.Active:
      return '活跃';
    case RuleStatus.Paused:
      return '暂停';
    case RuleStatus.Completed:
      return '已完成';
    case RuleStatus.Error:
      return '错误';
    default:
      return '未知';
  }
};

/**
 * 获取目标类型文本
 */
export const getTargetTypeText = (targetType: NotificationTargetType): string => {
  switch (targetType) {
    case NotificationTargetType.All:
      return '全部';
    case NotificationTargetType.Department:
      return '部门';
    case NotificationTargetType.Software:
      return '软件';
    case NotificationTargetType.Role:
      return '角色';
    default:
      return '未知';
  }
};

/**
 * 格式化时间显示
 */
export const formatTime = (timeString?: string): string => {
  if (!timeString) return '-';
  return new Date(timeString).toLocaleString('zh-CN');
};

/**
 * 格式化时间（仅时间部分）
 */
export const formatTimeOnly = (timeString?: string): string => {
  if (!timeString) return '-';
  return new Date(timeString).toLocaleTimeString('zh-CN', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

/**
 * 检查规则是否过期
 */
export const isRuleExpired = (rule: { endDate?: string }): boolean => {
  if (!rule.endDate) return false;
  return new Date(rule.endDate) < new Date();
};

/**
 * 计算距离结束还有多少天
 */
export const getDaysUntilEnd = (endDate?: string): number | null => {
  if (!endDate) return null;
  const now = new Date();
  const end = new Date(endDate);
  const diffTime = end.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};